/**
 * Directors decide whether Cubby support may look at the school, and for how
 * long. Nothing is visible to support without an active grant here.
 */

import { useState } from "react";
import { useSession } from "../auth/SessionProvider";
import { inputClass } from "../components/form";
import { Button, Card, Empty, Notice, PageHeader, Tag, selectClass } from "../components/ui";
import { describeApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useLoad } from "../lib/useLoad";

interface Grant {
  id: string;
  reason: string | null;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
  active: boolean;
}

export function SupportAccess() {
  const { api } = useSession();
  const { data, error, reload } = useLoad(() => api<{ grants: Grant[] }>("/api/support/grants"), [api]);
  const [hours, setHours] = useState(4);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  async function grant() {
    setBusy(true);
    setMessage(null);
    try {
      await api("/api/support/grants", { method: "POST", body: JSON.stringify({ hours, reason }) });
      setReason("");
      setMessage({ tone: "ok", text: `Support can see your school for the next ${hours} hours.` });
      reload();
    } catch (caught) {
      setMessage({ tone: "error", text: describeApiError(caught) });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/support/grants/${id}/revoke`, { method: "POST", body: "{}" });
      setMessage({ tone: "ok", text: "Access revoked." });
      reload();
    } catch (caught) {
      setMessage({ tone: "error", text: describeApiError(caught) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Support access"
        detail="Cubby support cannot see your school's records unless you allow it here. Every grant expires on its own."
      />
      {message ? <div className="mb-6"><Notice tone={message.tone}>{message.text}</Notice></div> : null}

      <Card className="mb-6">
        <h2 className="text-lg font-semibold">Let support look at your school</h2>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-sm font-medium text-stone-700">
            For
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className={`${selectClass} mt-1 block`}>
              <option value={1}>1 hour</option>
              <option value={4}>4 hours</option>
              <option value={24}>24 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
            </select>
          </label>
          <label className="min-w-[240px] flex-1 text-sm font-medium text-stone-700">
            Why (optional)
            <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Ticket about missing punches" className={`${inputClass} mt-1`} />
          </label>
          <Button disabled={busy} onClick={() => void grant()}>
            Grant access
          </Button>
        </div>
      </Card>

      {error ? <Notice>{error}</Notice> : null}
      <Card>
        <h2 className="text-lg font-semibold">History</h2>
        <div className="mt-4 space-y-2">
          {data?.grants.length === 0 ? <Empty>Support has never been given access.</Empty> : null}
          {data?.grants.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-200 px-4 py-3">
              <div>
                <p className="font-medium">{g.reason ?? "No reason given"}</p>
                <p className="text-sm text-stone-500">
                  Granted {formatDateTime(g.createdAt)},{" "}
                  {g.revokedAt ? `revoked ${formatDateTime(g.revokedAt)}` : `expires ${formatDateTime(g.expiresAt)}`}
                </p>
              </div>
              {g.active ? (
                <div className="flex items-center gap-2">
                  <Tag tone="warn">Active</Tag>
                  <Button variant="danger" size="sm" disabled={busy} onClick={() => void revoke(g.id)}>
                    Revoke now
                  </Button>
                </div>
              ) : (
                <Tag>Ended</Tag>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
